import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useState } from 'react';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qaff7nahb7382ov8n0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    email: siteInfo.about.contact.email,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />

      <div className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mb-16">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-foreground-600 leading-relaxed">
                {siteInfo.about.intro}
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden mb-12 h-[300px] md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20creative%20team%20workspace%20with%20colorful%20decor%20and%20plants%2C%20bright%20natural%20lighting%2C%20modern%20minimalist%20office%2C%20playful%20atmosphere%2C%20clean%20composition%2C%20no%20people&width=1600&height=700&seq=about-team-bg&orientation=landscape"
                alt="关于打屁股游戏团队"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
                <h2 className="font-heading text-2xl md:text-3xl text-background-50">我们的团队</h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                  <i className="ri-lightbulb-line text-xl"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让更多人发现并享受互动游戏的乐趣。我们相信简单的互动能带来最纯粹的快乐，通过精心整理的攻略和推荐，帮助每个人轻松找到适合自己的游戏。
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mb-4">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为最受欢迎的趣味游戏资讯平台。我们希望建立一个充满创意和欢乐的社区，让玩家可以分享游戏体验、交流玩法心得。
                </p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {siteInfo.about.reputation.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-xl p-5 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            i < item.rating
                              ? 'ri-star-fill text-amber-400'
                              : 'ri-star-line text-foreground-300'
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div>
                    <p className="font-heading text-sm text-foreground-800">{item.name}</p>
                    <p className="text-xs text-foreground-500">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何问题、建议或合作意向，欢迎随时联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-mail-line"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">邮箱</p>
                  <p className="font-medium text-sm text-foreground-800">{siteInfo.about.contact.email}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">微信</p>
                  <p className="font-medium text-sm text-foreground-800">{siteInfo.about.contact.wechat}</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500">
                    <i className="ri-qq-line"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">QQ</p>
                  <p className="font-medium text-sm text-foreground-800">{siteInfo.about.contact.qq}</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <h3 className="font-heading text-lg text-foreground-900 mb-6">发送消息</h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="font-heading text-lg text-foreground-800 mb-2">消息已发送！</p>
                    <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form=""
                    id="contact-form"
                    action={CONTACT_FORM_URL}
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    className="space-y-5"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const formData = new FormData(form);

                      const honeypot = formData.get('phone_alt') as string;
                      if (honeypot && honeypot.trim() !== '') {
                        setFormStatus('success');
                        return;
                      }
                      formData.delete('phone_alt');

                      try {
                        const params = new URLSearchParams();
                        formData.forEach((value, key) => {
                          params.append(key, value as string);
                        });

                        const resp = await fetch(CONTACT_FORM_URL, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: params.toString(),
                        });
                        const responseText = await resp.text();
                        let parsed: any = {};
                        try { parsed = JSON.parse(responseText); } catch { parsed = {}; }

                        if (resp.ok && parsed?.code === 'OK') {
                          setFormStatus('success');
                          setFormError('');
                          form.reset();
                        } else {
                          const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
                          if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
                            setFormError('提交失败，请稍后再试。');
                          } else {
                            setFormError(serverMsg || '提交失败，请稍后再试。');
                          }
                          setFormStatus('error');
                        }
                      } catch {
                        setFormError('网络错误，请稍后再试。');
                        setFormStatus('error');
                      }
                    }}
                  >
                    {formError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                        {formError}
                      </div>
                    )}
                    {formStatus === 'error' && !formError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                        提交失败，请检查网络后重试。
                      </div>
                    )}
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                    </div>
                    <div className="honeypot-field" aria-hidden="true">
                      <label htmlFor="phone_alt">备用电话</label>
                      <input
                        type="text"
                        id="phone_alt"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}